import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import {
  InlineDocsHint,
  PageTitle,
  PageDescription,
} from '@reflex/ui/components/layout/Typography';
import { toast } from 'sonner';
import { OrgOnboardingProfileSchema } from '@reflex/shared';
import { useAuth } from '@/hooks/auth/useAuth';
import { ApiError, api } from '@/lib/api';
import { orgSetupApi } from '@/lib/org-setup-api';
import {
  extractPluginBootstrapErrorDetail,
  type PluginBootstrapError,
} from '@/components/admin/plugin-bootstrap/PluginBootstrapError';
import { PluginBootstrapErrors } from '@/components/admin/plugin-bootstrap/PluginBootstrapErrors';
import { OrgOnboardingProfileSelector } from '@/components/admin/OrgOnboardingProfileSelector';
import { FormField } from '@/components/forms/FormField';
import { defaultOrgNameFromUser, defaultOrgSlugFromName } from '../defaultOrgName';
import { docs } from '../onboardingDocs';

// Email-domain claims are intentionally NOT collected here. They are a
// post-create concern (auto-join routing, identity claims) that an org
// admin manages from the org's admin surfaces with the `org:manage`
// permission. Forcing every new org to pick a domain at create time
// blocks self-serve onboarding for users who legitimately want a
// personal/sandbox org and have no domain to claim, and the server's
// `CreateOrganizationRequestSchema.emailDomains` has always been
// optional — the constraint lived only in this form.
const organizationSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  companySlug: z.string().regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and dashes only'),
  name: z.string().min(1, 'Organization name is required'),
  slug: z.string().regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and dashes only'),
  supportEmail: z.string().email('Must be a valid email').or(z.literal('')),
  orgOnboardingProfile: z.enum(OrgOnboardingProfileSchema.options, {
    message: 'Choose the kind of work you will use Reflex for',
  }),
});

type OrganizationFormValues = z.infer<typeof organizationSchema>;

/**
 * Create-org form: the only stage rendered in `mode="new"`. On success the
 * page hands off to existing mode via `onCreated`.
 */
export function OrganizationStage({
  onCreated,
}: {
  onCreated: (slug: string) => Promise<void> | void;
}) {
  const { user, memberships } = useAuth();
  const defaultCompanyName = defaultOrgNameFromUser(user);
  // Existing members reach this form via the org switcher's "Create
  // organization" action, so the first-run "your first organization"
  // framing only fits a user who has none yet.
  const title =
    memberships.length > 0 ? 'Create a new organization' : 'Create your first organization';
  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      companyName: defaultCompanyName,
      companySlug: defaultOrgSlugFromName(defaultCompanyName),
      name: '',
      slug: '',
      supportEmail: '',
      // Preselect software engineering: it is what the large majority of new
      // orgs pick, and its plugin set is a superset of the general one, so
      // the wrong default costs a user a click rather than a missing tool.
      // The field stays required in the schema — a user who clears it by
      // choosing nothing still can't submit — and the choice is reversible
      // from Admin → Plugins.
      orgOnboardingProfile: 'engineering',
    },
  });
  const selectedProfile = useWatch({ control, name: 'orgOnboardingProfile' });
  const companyName = useWatch({ control, name: 'companyName' });
  const companySlug = useWatch({ control, name: 'companySlug' });
  const organizationName = useWatch({ control, name: 'name' });
  const [companySlugEdited, setCompanySlugEdited] = useState(false);
  const [organizationSlugEdited, setOrganizationSlugEdited] = useState(false);

  useEffect(() => {
    if (!companySlugEdited) {
      setValue('companySlug', defaultOrgSlugFromName(companyName ?? ''), { shouldValidate: true });
    }
  }, [companyName, companySlugEdited, setValue]);

  useEffect(() => {
    if (!organizationSlugEdited) {
      const orgPart = defaultOrgSlugFromName(organizationName ?? '');
      setValue('slug', companySlug && orgPart ? `${companySlug}-${orgPart}` : '', {
        // Do not show an error on the untouched initial empty value. Once the
        // user supplies an organization name, validate the generated slug so
        // a prior submit error clears immediately.
        shouldValidate: Boolean(companySlug && orgPart),
      });
    }
  }, [companySlug, organizationName, organizationSlugEdited, setValue]);

  // Users never pick plugins here. The create form sends only the
  // self-described `orgOnboardingProfile`; the server derives the plugin
  // selection from it (single source of truth), so there's no catalog to fetch
  // and no plugins step in the wizard. A bootstrap error only appears if the
  // server rejects the derived selection — a Reflex-side config issue the user
  // can't fix.
  const [bootstrapError, setBootstrapError] = useState<PluginBootstrapError | undefined>();

  // Optional key transfer source: orgs where the user can manage secrets.
  // Copying needs `secrets:manage` on both the source (read) and the new org
  // (write); the creator always holds it on the new org, so we only filter
  // the source list here. The server re-checks both sides.
  const transferSources = useMemo(
    () => memberships.filter((m) => m.permissions.includes('secrets:manage')),
    [memberships],
  );
  const [copyFromOrgId, setCopyFromOrgId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (data: OrganizationFormValues) => {
      // Send only the self-described onboarding profile; the server derives and
      // installs the plugin selection from it (single source of truth). No
      // catalog fetch, no client-computed plugin list.
      const org = await api.createOrganization({
        companyName: data.companyName,
        companySlug: data.companySlug,
        name: data.name,
        slug: data.slug || undefined,
        supportEmail: data.supportEmail || undefined,
        orgOnboardingProfile: data.orgOnboardingProfile,
      });
      if (copyFromOrgId) {
        // Copy keys before handing off so the new org's setup state already
        // reflects them and the wizard can skip the Runloop step. A copy
        // failure is non-fatal: the org exists, so surface a toast and let
        // the user add keys in the wizard.
        try {
          const res = await orgSetupApi.copyKeyMaterial(org.id, copyFromOrgId);
          if (!res.runloopKeyCopied && res.modelKeysCopied === 0) {
            toast.info('No keys were available to copy from that organization.');
          } else {
            const parts: string[] = [];
            if (res.runloopKeyCopied) parts.push('Runloop key');
            if (res.modelKeysCopied > 0) {
              parts.push(`${res.modelKeysCopied} model key${res.modelKeysCopied === 1 ? '' : 's'}`);
            }
            toast.success(`Copied ${parts.join(' and ')}`);
          }
        } catch (err) {
          toast.error('Organization created, but copying keys failed', {
            description: err instanceof Error ? err.message : undefined,
          });
        }
      }
      return org;
    },
    onSuccess: async (org) => {
      toast.success('Organization created');
      // Awaiting keeps `mutation.isPending` true while the post-create
      // refresh runs, which prevents a stale-data flash on the next
      // wizard step.
      await onCreated(org.slug);
    },
    onError: (err) => {
      // 400s carrying structured plugin details surface inline because the
      // plugin slugs are the actionable bit; everything else is a toast.
      const detail =
        err instanceof ApiError && err.status === 400
          ? extractPluginBootstrapErrorDetail(err.body)
          : undefined;
      if (detail) {
        setBootstrapError(detail);
        return;
      }
      setBootstrapError(undefined);
      toast.error(err instanceof Error ? err.message : 'Failed to create organization');
    },
  });

  return (
    <>
      <PageTitle docs={docs('stage-1-organization')}>{title}</PageTitle>
      <PageDescription>
        Your company owns the subscription and shared security settings. Organizations keep work,
        membership, credentials, and plugin configuration separate.
      </PageDescription>
      <form
        onSubmit={handleSubmit((data) => {
          setBootstrapError(undefined);
          mutation.mutate(data);
        })}
        className="mt-6 space-y-4"
      >
        <div className="space-y-2">
          <Label htmlFor="company-name">Company name</Label>
          <Input id="company-name" placeholder="Geico" {...register('companyName')} />
          {errors.companyName && (
            <p className="text-xs text-destructive">{errors.companyName.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="company-slug">Company slug</Label>
          <Input
            id="company-slug"
            placeholder="geico"
            {...register('companySlug', {
              onChange: () => setCompanySlugEdited(true),
              setValueAs: (v: unknown) => String(v ?? '').toLowerCase(),
            })}
          />
          {errors.companySlug && (
            <p className="text-xs text-destructive">{errors.companySlug.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="org-name">
            Organization name <InlineDocsHint slug={docs('organization-name')} />
          </Label>
          <Input id="org-name" placeholder="Engineering" {...register('name')} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="org-slug">
            Organization URL slug <InlineDocsHint slug={docs('url-slug')} />
          </Label>
          <Input
            id="org-slug"
            placeholder="geico-engineering"
            {...register('slug', {
              onChange: () => setOrganizationSlugEdited(true),
              setValueAs: (v: unknown) => String(v ?? '').toLowerCase(),
            })}
          />
          {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="org-support-email">
            Support email (optional) <InlineDocsHint slug={docs('support-email')} />
          </Label>
          <Input id="org-support-email" type="email" {...register('supportEmail')} />
          {errors.supportEmail && (
            <p className="text-xs text-destructive">{errors.supportEmail.message}</p>
          )}
        </div>

        <FormField
          label={
            <>
              What kind of work will you use Reflex for? <InlineDocsHint slug={docs('work-type')} />
            </>
          }
          labelId="org-kind-label"
          required
          hint="Reflex uses this to set up relevant plugins. You can review and change them any time after setup."
          error={errors.orgOnboardingProfile?.message}
        >
          <OrgOnboardingProfileSelector
            value={selectedProfile}
            onChange={(profile) =>
              setValue('orgOnboardingProfile', profile, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            labelId="org-kind-label"
            required
          />
        </FormField>

        {/* A rejection here means the *derived* selection was invalid — a
            Reflex-side config issue the user can't fix, so surface the
            offending plugins for whoever's debugging rather than swallowing
            it. */}
        {bootstrapError && <PluginBootstrapErrors error={bootstrapError} />}

        {import.meta.env.DEV && transferSources.length > 0 && (
          <div className="space-y-2 rounded-md border p-3">
            <Label htmlFor="org-copy-keys" className="font-medium">
              Copy API keys from another organization
            </Label>
            <p className="text-xs text-muted-foreground">
              Optional. Copies the Runloop key and model provider keys so this org skips the Runloop
              step. You can edit them later.
            </p>
            <Select
              value={copyFromOrgId ?? 'none'}
              onValueChange={(v) => setCopyFromOrgId(v === 'none' ? null : v)}
            >
              <SelectTrigger id="org-copy-keys" data-testid="org-setup-copy-keys-source">
                <SelectValue placeholder="Don't copy keys" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't copy keys</SelectItem>
                {transferSources.map((m) => (
                  <SelectItem key={m.organization.id} value={m.organization.id}>
                    {m.organization.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending} data-testid="org-setup-create-submit">
            {mutation.isPending ? 'Creating…' : 'Create organization'}
          </Button>
        </div>
      </form>
    </>
  );
}
